// Pax Magellanic — Cinematic Starfield (codebase port)
// Drop-in replacement for src/components/visual/Starfield.tsx
//
// Replaces the old multi-mode Starfield AND obviates the separate Constellation
// canvas + the duplicate starfields inside PlanetaryFlyby. Mount ONCE at the
// page root, behind all content, fixed to the viewport. Scroll progress drives
// the journey: Departure → Warp → Cruise → Approach → Arrival.
//
// Usage in Home.tsx:
//
//   <div className="min-h-screen bg-background text-foreground">
//     <Navbar />
//     <Starfield />            {/* fixed inset-0, behind everything */}
//     <main>
//       <Hero />
//       <Services />
//       <Ideas />
//       <Leadership />
//       <Contact />
//     </main>
//   </div>
//
// You can delete Constellation.tsx entirely. PlanetaryFlyby.tsx should remove
// its <Starfield mode="drift" /> instances — the page-wide field already covers
// that section's background.

import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

export type StarfieldPalette = "magellanic" | "warm" | "cool";

export interface StarfieldProps {
  palette?: StarfieldPalette;
  /** Override total star count. Otherwise auto-picked by viewport. */
  starCountOverride?: number;
  /** Show the destination galaxy in the approach/arrival phase. Default true. */
  showGalaxy?: boolean;
  className?: string;
}

// ── math helpers ────────────────────────────────────────────────────────────
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const bell = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return Math.sin(t * Math.PI);
};

// ── phase curves ────────────────────────────────────────────────────────────
function warpIntensity(p: number) {
  if (p < 0.10) return 0;
  if (p < 0.30) return smoothstep(0.10, 0.30, p);
  if (p < 0.60) return 1;
  if (p < 0.85) return 1 - smoothstep(0.60, 0.85, p);
  return 0;
}
function approachIntensity(p: number) {
  if (p < 0.55) return 0;
  if (p < 0.95) return smoothstep(0.55, 0.95, p);
  return 1;
}
function cruiseIntensity(p: number) {
  return bell(0.20, 0.70, p);
}

interface Star {
  a: number; d: number; layer: 0 | 1 | 2;
  size: number; alpha: number; hue: number;
  twinkle: number; twinkleRate: number;
  spriteIdx: number;
}
interface Debris { a: number; d: number; speed: number; hue: number; }

const LAYER_SPEED = [0.18, 0.45, 1.0];
const LAYER_SIZE  = [0.5, 0.9, 1.4];
const LAYER_ALPHA = [0.55, 0.78, 1.0];

export function Starfield({
  palette = "magellanic",
  starCountOverride,
  showGalaxy = true,
  className,
}: StarfieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dustRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    const dust = dustRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !dust || !wrap) return;
    const ctx = canvas.getContext("2d");
    const dctx = dust.getContext("2d");
    if (!ctx || !dctx) return;

    // Cap DPR at 1.5: at 2x the per-frame fill-rate (full-canvas clears,
    // vignette, star glows) roughly doubles for little visible gain on a
    // mostly-dark animated field.
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let w = 0, h = 0, cx = 0, cy = 0;
    let raf = 0;
    let progress = 0;
    let stars: Star[] = [];
    let debris: Debris[] = [];
    let glowSprites: HTMLCanvasElement[] = [];
    let vignette: CanvasGradient | null = null;

    // Pre-rendered star-glow sprites, one per hue bucket. Drawing a cached
    // sprite per star (drawImage) is far cheaper than allocating a radial
    // gradient per star every frame — that allocation was the main-thread hot
    // path that made scrolling choppy.
    const SPRITE_HUES = [180, 210, 240, 270, 300, 340];
    const SPRITE_R = 16;
    function pickSprite(hue: number) {
      let idx = 0, best = Infinity;
      for (let i = 0; i < SPRITE_HUES.length; i++) {
        const d = Math.abs(hue - SPRITE_HUES[i]);
        if (d < best) { best = d; idx = i; }
      }
      return idx;
    }
    function buildGlowSprites() {
      glowSprites = SPRITE_HUES.map((hue) => {
        const s = document.createElement("canvas");
        s.width = s.height = Math.ceil(SPRITE_R * 2 * dpr);
        const g = s.getContext("2d")!;
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        const grad = g.createRadialGradient(SPRITE_R, SPRITE_R, 0, SPRITE_R, SPRITE_R, SPRITE_R);
        grad.addColorStop(0, `hsla(${hue}, 80%, 88%, 1)`);
        grad.addColorStop(0.35, `hsla(${hue}, 80%, 88%, 0.5)`);
        grad.addColorStop(1, `hsla(${hue}, 80%, 88%, 0)`);
        g.fillStyle = grad;
        g.beginPath(); g.arc(SPRITE_R, SPRITE_R, SPRITE_R, 0, Math.PI * 2); g.fill();
        // Bright core
        g.fillStyle = `hsla(${hue}, 70%, 95%, 1)`;
        g.beginPath(); g.arc(SPRITE_R, SPRITE_R, SPRITE_R * 0.32, 0, Math.PI * 2); g.fill();
        return s;
      });
    }

    function paintDust() {
      const dw = dust!.width, dh = dust!.height;
      dctx!.clearRect(0, 0, dw, dh);
      const baseGrad = dctx!.createLinearGradient(0, 0, 0, dh);
      baseGrad.addColorStop(0, "rgba(8,10,22,1)");
      baseGrad.addColorStop(1, "rgba(4,6,14,1)");
      dctx!.fillStyle = baseGrad;
      dctx!.fillRect(0, 0, dw, dh);

      const blobs =
        palette === "cool"
          ? [
              { x: 0.20, y: 0.30, r: 0.55, c: "rgba(40,90,180,0.40)" },
              { x: 0.80, y: 0.25, r: 0.50, c: "rgba(80,160,220,0.30)" },
              { x: 0.55, y: 0.75, r: 0.65, c: "rgba(40,40,120,0.50)" },
              { x: 0.92, y: 0.65, r: 0.40, c: "rgba(120,200,255,0.18)" },
            ]
          : palette === "warm"
          ? [
              { x: 0.20, y: 0.32, r: 0.60, c: "rgba(200,90,30,0.30)" },
              { x: 0.78, y: 0.22, r: 0.50, c: "rgba(255,180,90,0.25)" },
              { x: 0.55, y: 0.78, r: 0.65, c: "rgba(40,30,70,0.55)" },
              { x: 0.05, y: 0.65, r: 0.50, c: "rgba(120,40,160,0.25)" },
            ]
          : [
              { x: 0.18, y: 0.30, r: 0.55, c: "rgba(80,40,140,0.45)" },
              { x: 0.78, y: 0.22, r: 0.50, c: "rgba(220,90,40,0.35)" },
              { x: 0.55, y: 0.72, r: 0.65, c: "rgba(40,80,180,0.40)" },
              { x: 0.10, y: 0.85, r: 0.40, c: "rgba(180,60,90,0.30)" },
              { x: 0.92, y: 0.60, r: 0.45, c: "rgba(255,180,90,0.22)" },
            ];

      for (const b of blobs) {
        const g = dctx!.createRadialGradient(
          b.x * dw, b.y * dh, 0,
          b.x * dw, b.y * dh, b.r * Math.max(dw, dh),
        );
        g.addColorStop(0, b.c);
        g.addColorStop(1, "rgba(0,0,0,0)");
        dctx!.fillStyle = g;
        dctx!.fillRect(0, 0, dw, dh);
      }
      const grain = Math.floor((dw * dh) / 9000);
      dctx!.fillStyle = "rgba(255,255,255,0.04)";
      for (let i = 0; i < grain; i++) {
        dctx!.fillRect(Math.random() * dw, Math.random() * dh, 1, 1);
      }
    }

    function seed() {
      const isMobile = w < 760;
      const total = starCountOverride ?? (isMobile ? 150 : 300);
      stars = [];
      for (let i = 0; i < total; i++) {
        const layer: 0 | 1 | 2 =
          i < total * 0.55 ? 0 : i < total * 0.85 ? 1 : 2;
        const hue = 200 + Math.random() * 60 + (layer === 2 ? -40 + Math.random() * 100 : 0);
        stars.push({
          a: Math.random() * Math.PI * 2,
          d: 8 + Math.pow(Math.random(), 0.6) * Math.max(w, h) * 0.85,
          layer,
          size: LAYER_SIZE[layer] * (0.6 + Math.random() * 0.8),
          alpha: LAYER_ALPHA[layer] * (0.55 + Math.random() * 0.45),
          hue,
          twinkle: Math.random() * Math.PI * 2,
          twinkleRate: 0.6 + Math.random() * 1.4,
          spriteIdx: pickSprite(hue),
        });
      }
      debris = [];
      const dCount = isMobile ? 6 : 12;
      for (let i = 0; i < dCount; i++) {
        debris.push({
          a: Math.random() * Math.PI * 2,
          d: Math.random() * Math.max(w, h) * 0.7,
          speed: 1.4 + Math.random() * 1.8,
          hue: 28 + Math.random() * 18,
        });
      }
    }

    function resize() {
      const r = wrap!.getBoundingClientRect();
      w = r.width; h = r.height;
      cx = w / 2; cy = h * 0.42;
      canvas!.width = w * dpr; canvas!.height = h * dpr;
      canvas!.style.width = w + "px"; canvas!.style.height = h + "px";
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      const dw = w * 1.4, dh = h * 1.4;
      dust!.width = dw * dpr; dust!.height = dh * dpr;
      dust!.style.width = dw + "px"; dust!.style.height = dh + "px";
      dctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintDust();
      buildGlowSprites();
      seed();

      // Cache the vignette gradient (depends only on size) so we don't allocate
      // a full-canvas radial gradient every frame.
      vignette = ctx!.createRadialGradient(
        cx, cy, Math.min(w, h) * 0.3,
        cx, cy, Math.max(w, h) * 0.85,
      );
      vignette.addColorStop(0, "rgba(0,0,0,0)");
      vignette.addColorStop(1, "rgba(0,0,0,0.55)");
    }

    // ----- destination galaxy ---------------------------------------------
    // Painted ONCE into an offscreen canvas at first reveal (and on resize),
    // then composited each frame with rotation + tilt. ~14k stars + dust
    // lanes + HII regions are baked in — the per-frame cost is a single
    // drawImage. Visual recipe:
    //   1. Soft halo / disk haze
    //   2. Dense star cloud along log-spiral arms (bulge biased warm,
    //      disk biased blue, biased outward so disk fills out)
    //   3. Dust lanes carved with destination-out compositing
    //   4. HII regions — pink/magenta H-alpha clumps, additive blend
    //   5. Bright core / bulge with hot pinprick nucleus (additive)
    let galaxyCache: { canvas: HTMLCanvasElement; size: number; R: number } | null = null;

    function buildGalaxyCache(targetSize: number) {
      const PAD = 1.35;
      const S = Math.ceil(targetSize * 2 * PAD);
      const off = document.createElement("canvas");
      off.width = S; off.height = S;
      const g = off.getContext("2d")!;
      const cxg = S / 2, cyg = S / 2;
      const R = targetSize;

      // 1. Soft halo / disk haze
      const halo = g.createRadialGradient(cxg, cyg, R * 0.05, cxg, cyg, R * 1.15);
      halo.addColorStop(0,    "rgba(255,235,200,0.35)");
      halo.addColorStop(0.15, "rgba(220,200,230,0.22)");
      halo.addColorStop(0.45, "rgba(140,150,210,0.14)");
      halo.addColorStop(0.80, "rgba(60,70,140,0.06)");
      halo.addColorStop(1,    "rgba(0,0,0,0)");
      g.fillStyle = halo;
      g.fillRect(0, 0, S, S);

      // 2. Spiral star cloud
      const arms = 2;
      const b = 0.22;            // spiral pitch
      const armSpan = 6.5;       // theta range per arm
      const armWidthFactor = 0.16;
      // log spiral: r = a0 * e^(b*theta). Pick a0 so r=R at theta=armSpan.
      const a0 = R * Math.exp(-b * armSpan);

      // Seeded RNG so the galaxy is stable (we cache anyway).
      let seed = 1337;
      const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };

      const STAR_COUNT = 14000;
      for (let i = 0; i < STAR_COUNT; i++) {
        const isBulge = rnd() < 0.35;
        let x: number, y: number, brightness: number, hue: number;
        if (isBulge) {
          const u = Math.pow(rnd(), 2.3);
          const r = u * R * 0.42;
          const ang = rnd() * Math.PI * 2;
          x = cxg + Math.cos(ang) * r;
          y = cyg + Math.sin(ang) * r;
          hue = 30 + rnd() * 25;
          brightness = 0.55 + rnd() * 0.45;
        } else {
          const arm = Math.floor(rnd() * arms);
          const armPhase = (arm / arms) * Math.PI * 2;
          // Bias theta outward so the disk reads full-bodied
          const theta = Math.pow(rnd(), 0.7) * armSpan + 0.2;
          const baseR = a0 * Math.exp(b * theta);
          if (baseR > R * 1.1) continue;
          const scatter = (rnd() + rnd() - 1) * baseR * armWidthFactor;
          const interArm = rnd() < 0.18;
          const radialJitter = (rnd() - 0.5) * baseR * (interArm ? 0.7 : 0.18);
          const r = baseR + radialJitter;
          const tt = theta + armPhase + scatter / Math.max(baseR, 1);
          x = cxg + Math.cos(tt) * r;
          y = cyg + Math.sin(tt) * r;
          const u = r / R;
          hue = lerp(40, 220, Math.min(1, u + rnd() * 0.2));
          brightness = (interArm ? 0.25 : 0.7) + rnd() * 0.3;
          if (r > R * 0.95) brightness *= Math.max(0, 1 - (r - R * 0.95) / (R * 0.15));
        }
        const sz = rnd() < 0.04 ? 1.6 : (rnd() < 0.25 ? 1.0 : 0.6);
        g.fillStyle = `hsla(${hue}, 70%, 88%, ${brightness * 0.85})`;
        g.beginPath();
        g.arc(x, y, sz, 0, Math.PI * 2);
        g.fill();
      }

      // 3. Dust lanes — carved with destination-out
      g.save();
      g.globalCompositeOperation = "destination-out";
      for (let arm = 0; arm < arms; arm++) {
        const armPhase = (arm / arms) * Math.PI * 2;
        for (let theta = 0.5; theta < armSpan; theta += 0.012) {
          const baseR = a0 * Math.exp(b * theta) * 0.93;
          if (baseR > R * 1.05 || baseR < R * 0.18) continue;
          const tt = theta + armPhase;
          const x = cxg + Math.cos(tt) * baseR;
          const y = cyg + Math.sin(tt) * baseR;
          const dustR = baseR * 0.06 + 4;
          const dust = g.createRadialGradient(x, y, 0, x, y, dustR);
          const rimFade = 1 - Math.max(0, (baseR - R * 0.7) / (R * 0.4));
          dust.addColorStop(0, `rgba(0,0,0,${0.55 * rimFade})`);
          dust.addColorStop(1, "rgba(0,0,0,0)");
          g.fillStyle = dust;
          g.beginPath();
          g.arc(x, y, dustR, 0, Math.PI * 2);
          g.fill();
        }
      }
      g.restore();

      // 4. HII regions — pink/magenta H-alpha clumps, additive
      g.save();
      g.globalCompositeOperation = "lighter";
      for (let arm = 0; arm < arms; arm++) {
        const armPhase = (arm / arms) * Math.PI * 2;
        for (let theta = 0.6; theta < armSpan; theta += 0.06 + rnd() * 0.08) {
          if (rnd() > 0.6) continue;
          const baseR = a0 * Math.exp(b * theta);
          if (baseR > R * 1.0 || baseR < R * 0.28) continue;
          const jitterT = (rnd() - 0.5) * 0.12;
          const jitterR = (rnd() - 0.5) * baseR * 0.05;
          const tt = theta + armPhase + jitterT;
          const r = baseR + jitterR;
          const x = cxg + Math.cos(tt) * r;
          const y = cyg + Math.sin(tt) * r;
          const blobR = 4 + rnd() * 9;
          const grad = g.createRadialGradient(x, y, 0, x, y, blobR);
          const hueChoice = rnd() < 0.7 ? 335 : (rnd() < 0.5 ? 280 : 200);
          grad.addColorStop(0, `hsla(${hueChoice}, 95%, 85%, 0.95)`);
          grad.addColorStop(0.3, `hsla(${hueChoice}, 90%, 70%, 0.55)`);
          grad.addColorStop(1, "rgba(0,0,0,0)");
          g.fillStyle = grad;
          g.beginPath();
          g.arc(x, y, blobR, 0, Math.PI * 2);
          g.fill();
        }
      }
      g.restore();

      // 5. Bright core / bulge — additive, dimmed 25% from initial pass
      g.save();
      g.globalCompositeOperation = "lighter";
      const core = g.createRadialGradient(cxg, cyg, 0, cxg, cyg, R * 0.32);
      core.addColorStop(0,    "rgba(255,245,210,0.75)");
      core.addColorStop(0.20, "rgba(255,210,140,0.64)");
      core.addColorStop(0.55, "rgba(220,150,90,0.26)");
      core.addColorStop(1,    "rgba(120,60,40,0)");
      g.fillStyle = core;
      g.beginPath();
      g.arc(cxg, cyg, R * 0.32, 0, Math.PI * 2);
      g.fill();
      const nucleus = g.createRadialGradient(cxg, cyg, 0, cxg, cyg, R * 0.06);
      nucleus.addColorStop(0, "rgba(255,255,240,0.75)");
      nucleus.addColorStop(1, "rgba(255,220,180,0)");
      g.fillStyle = nucleus;
      g.beginPath();
      g.arc(cxg, cyg, R * 0.06, 0, Math.PI * 2);
      g.fill();
      g.restore();

      return { canvas: off, size: S, R };
    }

    function drawGalaxy(p: number, t: number) {
      if (!showGalaxy) return;
      const a = approachIntensity(p);
      if (a <= 0) return;
      const size = lerp(8, Math.min(w, h) * 0.3, a);
      const op = clamp01(smoothstep(0, 1, a));

      const targetSize = Math.min(w, h) * 0.3;
      if (!galaxyCache || Math.abs(galaxyCache.R - targetSize) > 4) {
        galaxyCache = buildGalaxyCache(targetSize);
      }

      ctx!.save();
      ctx!.translate(cx, cy);
      ctx!.rotate(t * 0.00003 + p * 0.25);
      // Slight tilt — true galaxies are rarely perfectly face-on
      ctx!.transform(1, 0, 0, 0.78, 0, 0);
      ctx!.globalAlpha = op;
      ctx!.globalCompositeOperation = "screen";
      const scale = size / galaxyCache.R;
      const drawSize = galaxyCache.size * scale;
      ctx!.drawImage(
        galaxyCache.canvas,
        -drawSize / 2, -drawSize / 2,
        drawSize, drawSize,
      );
      ctx!.globalAlpha = 1;
      ctx!.globalCompositeOperation = "source-over";
      ctx!.restore();

      // Reticle — drawn unrotated and untilted so it stays HUD-anchored
      const lockA = smoothstep(0.85, 1.0, p);
      if (lockA > 0) {
        ctx!.save();
        ctx!.translate(cx, cy);
        ctx!.strokeStyle = `rgba(255,170,80,${0.85 * lockA})`;
        ctx!.lineWidth = 1;
        const r1 = size * 0.90, r2 = size * 1.05;
        for (let q = 0; q < 4; q++) {
          ctx!.beginPath();
          ctx!.arc(0, 0, r1, q * Math.PI / 2 + 0.2, q * Math.PI / 2 + Math.PI / 2 - 0.2);
          ctx!.stroke();
        }
        ctx!.strokeStyle = `rgba(255,200,140,${0.7 * lockA})`;
        for (let i = 0; i < 8; i++) {
          const ang = (i / 8) * Math.PI * 2;
          ctx!.beginPath();
          ctx!.moveTo(Math.cos(ang) * r1, Math.sin(ang) * r1);
          ctx!.lineTo(Math.cos(ang) * r2, Math.sin(ang) * r2);
          ctx!.stroke();
        }
        ctx!.restore();
      }
    }

    // Cap the animation at ~30fps. A background starfield doesn't need 60fps,
    // and halving the per-second draw work leaves the main thread far freer to
    // handle scrolling smoothly.
    let lastDraw = 0;
    const FRAME_MS = 1000 / 30;
    function draw(now: number) {
      raf = requestAnimationFrame(draw);
      if (now - lastDraw < FRAME_MS) return;
      lastDraw = now;

      const W = warpIntensity(progress);
      const A = approachIntensity(progress);
      const C = cruiseIntensity(progress);

      const hx = Math.sin(now * 0.0002 + progress * 6) * 0.02 + Math.sin(now * 0.00007) * 0.01;
      const ox = cx + hx * w * 0.35;
      const oy = cy + Math.sin(now * 0.00009) * h * 0.04;

      dust!.style.transform =
        `translate3d(${-hx * w * 0.4 - w * 0.2}px, ${-h * 0.2 + Math.sin(now * 0.00006) * h * 0.03}px, 0) scale(${1 + A * 0.08})`;
      dust!.style.opacity = String(0.85 + C * 0.15);

      const fade = lerp(0.32, 0.08, W * 0.9);
      ctx!.fillStyle = `rgba(4,6,14,${fade})`;
      ctx!.fillRect(0, 0, w, h);

      const baseSpeed = 0.06 + W * 14 + C * 0.4;
      const streakBase = W * 70;
      const deadR = Math.min(w, h) * (0.16 + C * 0.10);
      const deadFeather = Math.min(w, h) * 0.08;

      ctx!.lineCap = "round";

      for (const s of stars) {
        s.d += baseSpeed * LAYER_SPEED[s.layer];
        const x = ox + Math.cos(s.a) * s.d;
        const y = oy + Math.sin(s.a) * s.d;
        if (x < -30 || x > w + 30 || y < -30 || y > h + 30) {
          s.a = Math.random() * Math.PI * 2;
          s.d = 4 + Math.random() * 24;
          s.twinkle = Math.random() * Math.PI * 2;
          continue;
        }
        const tw = 0.55 + 0.45 * Math.sin(now * 0.001 * s.twinkleRate + s.twinkle);
        const twinkleMix = lerp(tw, 1, W);
        const distFromCenter = Math.hypot(x - cx, y - cy);
        const centerFade = smoothstep(deadR, deadR + deadFeather, distFromCenter);
        const alpha = s.alpha * twinkleMix * centerFade;

        if (W > 0.05) {
          const dist = Math.min(1, s.d / Math.max(w, h) * 1.6);
          const len = streakBase * LAYER_SPEED[s.layer] * (0.4 + dist);
          const sx = x - Math.cos(s.a) * len;
          const sy = y - Math.sin(s.a) * len;
          const g = ctx!.createLinearGradient(sx, sy, x, y);
          g.addColorStop(0, `hsla(${s.hue}, 80%, 75%, 0)`);
          g.addColorStop(1, `hsla(${s.hue}, 90%, 85%, ${alpha})`);
          ctx!.strokeStyle = g;
          ctx!.lineWidth = s.size * (0.7 + W * 1.1);
          ctx!.beginPath(); ctx!.moveTo(sx, sy); ctx!.lineTo(x, y); ctx!.stroke();
        } else {
          // Cached glow sprite instead of a per-star radial gradient.
          const r = s.size * 3;
          ctx!.globalAlpha = alpha;
          ctx!.drawImage(glowSprites[s.spriteIdx], x - r, y - r, r * 2, r * 2);
          ctx!.globalAlpha = 1;
        }
      }

      if (C > 0.1) {
        for (const d of debris) {
          d.d += d.speed * (8 + W * 30 + C * 6);
          const x = ox + Math.cos(d.a) * d.d;
          const y = oy + Math.sin(d.a) * d.d;
          if (x < -50 || x > w + 50 || y < -50 || y > h + 50) {
            d.a = Math.random() * Math.PI * 2;
            d.d = Math.min(w, h) * 0.32 + Math.random() * 80;
          }
          const dc = Math.hypot(x - cx, y - cy);
          const centerFade = smoothstep(deadR, deadR + deadFeather * 1.5, dc);
          if (centerFade < 0.05) continue;
          const len = 22 + W * 80 + C * 14;
          const sx = x - Math.cos(d.a) * len;
          const sy = y - Math.sin(d.a) * len;
          const g = ctx!.createLinearGradient(sx, sy, x, y);
          g.addColorStop(0, `hsla(${d.hue}, 95%, 65%, 0)`);
          g.addColorStop(0.7, `hsla(${d.hue}, 95%, 70%, ${0.32 * C * centerFade})`);
          g.addColorStop(1, `hsla(${d.hue}, 95%, 85%, ${0.7 * C * centerFade})`);
          ctx!.strokeStyle = g;
          ctx!.lineWidth = 1.1 + C * 1.0;
          ctx!.beginPath(); ctx!.moveTo(sx, sy); ctx!.lineTo(x, y); ctx!.stroke();
        }
      }

      drawGalaxy(progress, now);

      if (vignette) {
        ctx!.fillStyle = vignette;
        ctx!.fillRect(0, 0, w, h);
      }
    }

    // Cache the page's scrollable range. Reading scrollHeight on every scroll
    // event would force a layout reflow on the main thread; we only need to
    // recompute when the document's height actually changes.
    let scrollMax = 0;
    function recalcScrollMax() {
      scrollMax = document.documentElement.scrollHeight - window.innerHeight;
    }
    function onScroll() {
      progress = scrollMax > 0 ? clamp01(window.scrollY / scrollMax) : 0;
    }

    resize();
    recalcScrollMax();
    onScroll();
    if (!reduced) raf = requestAnimationFrame(draw);
    else {
      // Static fallback — single paint
      ctx.fillStyle = "#04060e"; ctx.fillRect(0, 0, w, h);
    }

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    // Recompute the cached scroll range when the viewport resizes or the
    // document's body changes height (e.g. lazy images load below the fold).
    const docRO = new ResizeObserver(recalcScrollMax);
    docRO.observe(document.body);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalcScrollMax, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      docRO.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalcScrollMax);
    };
  }, [palette, starCountOverride, showGalaxy, reduced]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={
        className ??
        "fixed inset-0 -z-10 pointer-events-none overflow-hidden"
      }
    >
      <canvas
        ref={dustRef}
        style={{
          position: "absolute",
          left: 0, top: 0,
          willChange: "transform, opacity",
          filter: "blur(40px) saturate(1.1)",
          mixBlendMode: "screen",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
    </div>
  );
}
